import React, { useEffect, useState } from 'react';

import { matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useGetRolePermissionsQuery,
  useGetRolesQuery,
} from '@school_ify/schoolify-repository/src/teacher/types';

import useForm from 'hooks/useForm';
import ModalDialog from 'components/ModalDialog/ModalDialog';
import TextField from 'clean/components/common/TextField/TextField';
import CheckBox from 'clean/components/common/CheckBox/CheckBox';

import './UserDetails.scss';

interface Props {
  onClose: () => void;
  onSave: () => void;
}

const RoleDetails: React.FC<Props> = ({ onClose, onSave }) => {
  const { t } = useTranslation();
  const [rolesEnabled, setRolesEnabled] = useState<string[]>([]);
  const [title, setTitle] = useState('');

  const [formIsTouched, setFormIsTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const location = useLocation();
  const match = matchPath<{ id: string }>(location.pathname, ['/permissions/users/:id']);

  const userId = match.params.id;

  const { data: roles, loading: loadingRoles } = useGetRolesQuery();
  const { data: role, loading: loadingRole } = useGetRolePermissionsQuery({
    variables: {
      rolesByPkId: userId,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (role) {
      setTitle(role.roles_by_pk.title);
      setRolesEnabled(role.roles_by_pk.permissions.map(p => p.permission_id));
    }
  }, [role]);

  const onCheck = (roleId: string) => {
    const isChecked = rolesEnabled.includes(roleId);
    if (isChecked) {
      setRolesEnabled(rolesEnabled.filter(role => role !== roleId));
    } else {
      setRolesEnabled([...rolesEnabled, roleId]);
    }
  };

  const form = useForm({
    errorHandler: () => null,
    isLoadingInitials: loadingRoles || loadingRole,
    isLoadingActions: isSaving,
  });

  const onConfirm = async () => {
    setFormIsTouched(true);
    if (!form.isValid) {
      return;
    }
    try {
      setIsSaving(true);

      onSave();
      onClose();
    } catch (err) {
      console.warn(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalDialog
      open={true}
      isLoadingContent={form.isLoadingInitials}
      isLoadingActions={form.isLoadingActions}
      onClose={onClose}
      title={t('permissions:UserDetailsEditUser')}
      error={!form.isValid && formIsTouched ? t('common:all_fileds') : ''}
      actions={[
        {
          type: 'filled',
          colour: 'primary',
          fullWidth: true,
          label: t('permissions:UserDetailsUpdate'),
          handler: onConfirm,
        },
      ]}
    >
      <div className="userDetails">
        <div className="defaultHorizontalPadding">
          <TextField
            label={t('permissions:UserDetailsName')}
            disabled={true}
            errors={form.errors}
            formIsTouched={formIsTouched}
            property="title"
            value={title}
            onChange={e => {
              setTitle(e.target.value);
            }}
          />
        </div>
        <div className="line"></div>
        <div className="defaultHorizontalPadding">
          <div className="title">{t('permissions:UserDetailsRoles')}</div>
        </div>
        <div>
          {roles?.roles.map(role => {
            return (
              <div key={role.id} className="item" onClick={() => onCheck(role.id)}>
                <CheckBox
                  color="orange"
                  onChange={() => null}
                  checked={rolesEnabled.includes(role.id)}
                  style={{ pointerEvents: 'none' }}
                />
                <div className="label">{role.title}</div>
              </div>
            );
          })}
        </div>
      </div>
    </ModalDialog>
  );
};

export default RoleDetails;
